import React, { useState } from "react";
import { toast } from "react-toastify";
import Pagination from "../../components/ui/Pagination";
import { dtrApi } from "../../api/dtrApi";
import {
  useCutoffEmployees,
  useDtrExports,
} from "../../api/queries/approvalQueries";
import { saveFile } from "../../api/adminToolsApi";
import ExportCheckDialog from "./ExportCheckDialog";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime, formatPlainDate } from "../../utils/dateTime";

const EMPTY = [];

// kind: undefined = our CSV; "dtr" / "overtime" = the EasyPay payroll files
const download = async (id, kind) => {
  try {
    saveFile(
      await (kind
        ? dtrApi.downloadEasyPay(id, kind)
        : dtrApi.downloadExport(id)),
    );
  } catch (err) {
    toast.error(getApiErrorMessage(err, "Could not download the file."));
  }
};

/**
 * Time → Export: send a cut-off to payroll. Only employees HR approved are
 * exported; their days are locked afterwards. Past exports can be downloaded
 * again.
 */
const ExportTab = ({ period, shared }) => {
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const scope = {
    companyId: shared.companyId || undefined,
    branchId: shared.branchId || undefined,
    assignmentType: shared.assignmentType || undefined,
  };
  // The cut-off's numbers for the current filters (who is ready)
  const status = useCutoffEmployees(
    period
      ? { ...period, ...scope, withEntries: "true", page: 1, limit: 1 }
      : null,
    { enabled: !!period },
  );
  const summary = status.data?.summary || {};
  const exportsQuery = useDtrExports({ page: historyPage, limit: 10 });
  const history = exportsQuery.data?.data || EMPTY;
  // The pre-export dialog checks the cut-off, settles days, then exports
  const onExported = (res) => {
    setConfirm(false);
    setResult(res);
    toast.success(
      `Exported ${res?.exported ?? res?.rowCount ?? 0} employee(s): processed.` +
        (res?.approvedAtExport
          ? ` ${res.approvedAtExport} approved now.`
          : "") +
        (res?.includedForPaidLeave
          ? ` ${res.includedForPaidLeave} for paid leave only.`
          : "") +
        (res?.corrections ? ` ${res.corrections} correction(s).` : ""),
    );
  };

  return (
    <>
      <div className="att-card mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="fw-semibold">
              {status.data?.period?.label || "Cut-off"}
            </div>
            <div className="d-flex flex-wrap gap-2 mt-1">
              <span className="status-pill status-pill--green">
                Approved: {summary.approved ?? 0}
              </span>
              <span className="status-pill status-pill--gray">
                Open: {summary.open ?? 0}
              </span>
              <span className="status-pill status-pill--blue">
                Processed: {summary.exported ?? 0}
              </span>
              {summary.changedAfterApproval > 0 && (
                <span className="status-pill status-pill--amber">
                  Changed after approval: {summary.changedAfterApproval}
                </span>
              )}
            </div>
            <p className="fs-075 text-slate-500 mt-2 mb-0">
              Exports everyone with time entries or paid leave in this cut-off,
              and approves them. Processed (exported) days are locked.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={() => setConfirm(true)}
            disabled={!period}
          >
            <i className="bi bi-box-arrow-up"></i> Export for payroll
          </button>
        </div>
        {result && (
          <div className="alert alert-info fs-080 py-2 mt-3 mb-0">
            Exported <strong>{result.exported ?? result.rowCount ?? 0}</strong>{" "}
            employee(s).
            {result.approvedAtExport > 0 &&
              ` ${result.approvedAtExport} approved at export.`}
            {result.includedForPaidLeave > 0 &&
              ` ${result.includedForPaidLeave} included for paid leave only.`}
            {result.corrections > 0 &&
              ` ${result.corrections} correction(s) of reopened cut-offs.`}
            {result.alreadyExported > 0 &&
              ` ${result.alreadyExported} were already exported.`}
          </div>
        )}
      </div>

      <h2 className="bio-subtitle">Export history</h2>
      <div className="glass-table-card">
        {exportsQuery.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(exportsQuery.error, "Could not load exports.")}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table mb-0 att-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">Cut-off</th>
                    <th className="py-3">Branch</th>
                    <th className="py-3 text-end">Employees</th>
                    <th className="py-3">Exported</th>
                    <th className="py-3 pe-4 text-end">File</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {history.map((x) => (
                    <tr key={x.id}>
                      <td className="ps-4 fw-semibold text-nowrap">
                        {formatPlainDate(x.periodStart)} –{" "}
                        {formatPlainDate(x.periodEnd)}
                        {x.corrections > 0 && (
                          <span
                            className="status-pill status-pill--amber ms-2"
                            title="Re-export of reopened cut-offs: corrected totals for these employees"
                          >
                            Correction: {x.corrections}
                          </span>
                        )}
                      </td>
                      <td>
                        {x.branchName ||
                          (x.branchId ? "One branch" : "All branches")}
                      </td>
                      <td className="text-end tabular-nums">{x.rowCount}</td>
                      <td className="text-nowrap">
                        {formatDateTime(x.createdAt)}
                        {x.createdBy?.name ? ` · ${x.createdBy.name}` : ""}
                      </td>
                      <td className="pe-4 text-end">
                        <div className="d-inline-flex flex-wrap justify-content-end gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-light border att-action-btn"
                            onClick={() => download(x.id, "dtr")}
                            title="EasyPay import file: regular hours, late and undertime minutes"
                          >
                            <i className="bi bi-file-earmark-text"></i> EasyPay
                            DTR
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-light border att-action-btn"
                            onClick={() => download(x.id, "overtime")}
                            title="EasyPay import file: overtime, rest-day and night-differential hours"
                          >
                            <i className="bi bi-file-earmark-text"></i> EasyPay
                            Overtime
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-light border att-action-btn"
                            onClick={() => download(x.id)}
                          >
                            <i className="bi bi-file-earmark-spreadsheet"></i>{" "}
                            CSV
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!exportsQuery.isLoading && history.length === 0 && (
                    <tr>
                      <td colSpan={5} className="approval-empty">
                        <i className="bi bi-archive"></i>No exports yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={historyPage}
              limit={10}
              total={exportsQuery.data?.total || 0}
              onPageChange={setHistoryPage}
              onLimitChange={() => {}}
              pageSizes={[10]}
            />
          </>
        )}
      </div>

      {confirm && period && (
        <ExportCheckDialog
          period={period}
          scope={scope}
          label={status.data?.period?.label}
          onClose={() => setConfirm(false)}
          onExported={onExported}
        />
      )}
    </>
  );
};

export default ExportTab;
